import { NumberInputInputProps, NumberInputRootProps } from '@chakra-ui/react';
import {
  IOfferingRes,
  IQuoteItem,
  IQuoteOfferingReqSchema,
  IQuoteRespSchema,
  IRateResSchema,
  QuoteAmendmentVersionEnum,
  QuoteStatusEnum,
  QuoteTypeEnum,
} from '@monetize/types/app';
import { ExperienceConfigurationKeys } from '@monetize/types/experience-config';
import { formatInteger } from '@monetize/utils/core';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { UseFormSetError, UseFormSetValue } from 'react-hook-form';
import {
  DEFAULT_QUOTE_COLORS,
  QUOTE_QUANTITY_CELL_GRID_TEMPLATE,
  QUOTE_QUANTITY_TEXT_INSET,
} from '../../constants/quotes';
import { useExperienceConfiguration } from '../../hooks';
import { QuantityText } from '../../routes/Quotes/Quote/components/QuantityText';
import { useQuoteContext } from '../../routes/Quotes/Quote/quoteContext';
import { getQuoteColors, isQuoteQtyChanged } from '../../utils';
import {
  formatQuantityRangeTooltip,
  isProductNonProrated,
} from '../../utils/quotes';
import {
  MAdditionNumberInput,
  MCustomNumberInput,
  MGrid,
  MTooltip,
} from '../Monetize';

/** Fills the wrapper's fixed track so the tooltip anchors to the input box. */
const TOOLTIP_ANCHOR_STYLE = { width: '100%' };

/**
 * Comma-grouped whole numbers, matching `formatInteger`. The number input
 * parses its controlled `value` before displaying it — with `parseFloat`
 * unless `formatOptions` is set — so a pre-formatted "12,345" rendered as 12.
 * The raw value goes in and zag does the grouping instead.
 */
export const COMMA_QTY_FORMAT: Intl.NumberFormatOptions = {
  maximumFractionDigits: 0,
};

interface QuantityCellProps extends Omit<NumberInputRootProps, 'onChange'> {
  index: number;
  quote: IQuoteRespSchema | null;
  item: IQuoteItem;
  value: string;
  isScheduledChange: boolean;
  scheduledChangePriorQty?: number;
  isAmendment: boolean;
  isRenewal: boolean;
  placeholder: string;
  offeringRate: IRateResSchema | null;
  watchRateId: string | null | undefined;
  isLoading: boolean;
  isOfferingLoading: boolean;
  offering: IOfferingRes;
  isRemoved?: boolean;
  setValue: UseFormSetValue<IQuoteOfferingReqSchema>;
  setError: UseFormSetError<IQuoteOfferingReqSchema>;
  handleBlur: (add: boolean) => void;
  handleQuoteOfferingWithoutDirtyCheck: () => void;
  isDisabled?: boolean;
  isReadOnly?: boolean;
  onChange?: (valueAsString: string, valueAsNumber: number) => void;
}

const QuantityCell: FC<QuantityCellProps> = React.forwardRef<
  any,
  QuantityCellProps
>(
  (
    {
      index,
      item,
      quote,
      isScheduledChange,
      scheduledChangePriorQty,
      isAmendment,
      isRenewal,
      value,
      offeringRate,
      offering,
      watchRateId,
      isLoading,
      isOfferingLoading,
      isRemoved,
      isDisabled,
      isReadOnly,
      handleBlur,
      setValue,
      setError,
      handleQuoteOfferingWithoutDirtyCheck,
      ...rest
    }: QuantityCellProps,
    ref: any,
  ): React.ReactElement | null => {
    const { showCommaFormattedQty } = useExperienceConfiguration(
      ExperienceConfigurationKeys.quoteAndContractSettings,
    );
    const isNew = !isAmendment;
    const isDraft = quote?.status === QuoteStatusEnum.DRAFT;
    // If it's amend v2 and editable, just make input editable by default
    const shouldEditByDefault =
      (!isDisabled &&
        quote?.amendmentVersion === QuoteAmendmentVersionEnum.v2) ||
      isNew;
    const [editMode, setEditMode] = useState<boolean>(shouldEditByDefault);
    const [focused, setFocused] = useState<boolean>(shouldEditByDefault);
    const [isEditing, setIsEditing] = useState(false);
    /** Derived, not state — as state it renders blank (placeholder) for a frame
     * before the effect populates it, and trails `value` by a render. */
    const formattedQuantity = useMemo(() => formatInteger(value), [value]);
    const isNonProratedProduct = isProductNonProrated(
      offering,
      item?.productId!,
    );

    const priceRange =
      item.productId != null
        ? offeringRate?.productPriceRanges?.[item.productId]
        : undefined;
    const tooltipLabel = priceRange
      ? formatQuantityRangeTooltip(priceRange)
      : '';

    useEffect(() => {
      if (shouldEditByDefault) {
        setEditMode(true);
        setFocused(true);
      } else {
        setEditMode(false);
        setFocused(false);
      }
    }, [shouldEditByDefault]);

    const checkQtyChanged = isQuoteQtyChanged(
      item,
      Number(value),
      isScheduledChange ? scheduledChangePriorQty : item.previousQuantity!,
    );
    const quoteType = quote?.type || QuoteTypeEnum.NEW;
    const { useAmendmentV2 } = useQuoteContext();
    const quoteColors =
      checkQtyChanged && !useAmendmentV2
        ? getQuoteColors(quoteType)
        : DEFAULT_QUOTE_COLORS;

    const styleProps: NumberInputInputProps = {
      width: '100%',
      textAlign: 'left',
      ...QUOTE_QUANTITY_TEXT_INSET,
      fontWeight: '400',
      onClick: (ev: React.MouseEvent<HTMLInputElement>) => {
        const inputElement = ev.currentTarget;
        if (inputElement.value === '0') {
          inputElement.select();
        }
      },
      ...quoteColors,
    };

    const handleInputBlur = (add?: boolean) => {
      setIsEditing(false);
      if (!shouldEditByDefault) {
        setEditMode(false);
        setFocused(false);
      }
      handleQuoteOfferingWithoutDirtyCheck();
    };

    const renderNumberInput = () => {
      const commonProps = {
        styleProps,
        value,
        // Ungrouped while editing so typing is not fought.
        formatOptions:
          showCommaFormattedQty && !isEditing ? COMMA_QTY_FORMAT : undefined,
        variant: (isReadOnly ? 'readonly' : 'quantity') as
          | 'readonly'
          | 'quantity',
        'aria-label': rest.name,
        isDisabled: isRemoved || isDisabled,
        isReadOnly,
        onFocus: () => setIsEditing(true),
        resetEmptyValueOnBlur: true,
        ...rest,
      };

      // Render MAdditionNumberInput for scheduled changes or amendments
      if (isScheduledChange || isAmendment) {
        return (
          <MAdditionNumberInput
            {...commonProps}
            quoteColors={
              isAmendment && (isNew || useAmendmentV2) ? {} : quoteColors
            }
            iconColor="tBlue.magenta"
            handleInputBlur={handleInputBlur}
            previousQuantity={
              isScheduledChange
                ? scheduledChangePriorQty
                : item?.previousQuantity
            }
          />
        );
      }

      // Default: render MCustomNumberInput
      return (
        <MCustomNumberInput {...commonProps} onBlur={() => handleInputBlur()} />
      );
    };

    // For non-prorated products, always show QuantityText (read-only)
    if (isNonProratedProduct) {
      return (
        <MGrid
          justifyContent="center"
          alignItems="center"
          gridTemplateColumns={QUOTE_QUANTITY_CELL_GRID_TEMPLATE}
          data-testid={`quantity-wrap-${index}`}
        >
          <QuantityText
            value={showCommaFormattedQty ? formattedQuantity : value}
            quote={quote}
            item={item}
            scheduledChangePriorQty={scheduledChangePriorQty}
            type={quoteType}
            isScheduledChange={isScheduledChange}
            isRemoved={isRemoved}
            hideDifference={useAmendmentV2}
            isNonProratedProduct={isNonProratedProduct}
          />
        </MGrid>
      );
    }

    return (
      <MGrid
        onMouseEnter={() =>
          !isRemoved &&
          !isNew &&
          isDraft &&
          !isDisabled &&
          !isReadOnly &&
          setEditMode(true)
        }
        onMouseLeave={() =>
          !isRemoved &&
          !isNew &&
          isDraft &&
          !isDisabled &&
          !isReadOnly &&
          setEditMode(false)
        }
        onMouseDown={() =>
          !isRemoved &&
          !isNew &&
          isDraft &&
          !isDisabled &&
          !isReadOnly &&
          setFocused(true)
        }
        justifyContent="center"
        alignItems="center"
        gridTemplateColumns={QUOTE_QUANTITY_CELL_GRID_TEMPLATE}
        data-testid={`quantity-wrap-${index}`}
      >
        {focused || (!focused && editMode) ? (
          <MTooltip
            isOpen={isEditing && !!tooltipLabel}
            label={tooltipLabel}
            placement="top"
          >
            <div style={TOOLTIP_ANCHOR_STYLE}>{renderNumberInput()}</div>
          </MTooltip>
        ) : (
          <QuantityText
            value={showCommaFormattedQty ? formattedQuantity : value}
            quote={quote}
            item={item}
            scheduledChangePriorQty={scheduledChangePriorQty}
            type={quoteType}
            isScheduledChange={isScheduledChange}
            isRemoved={isRemoved}
            hideDifference={useAmendmentV2}
            isNonProratedProduct={isNonProratedProduct}
          />
        )}
      </MGrid>
    );
  },
);

export default QuantityCell;
